import { makeStyles } from '@material-ui/core/styles';
import useAsync from 'react-use/esm/useAsync';
import {
  RELATION_OWNED_BY,
  parseEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { useApi } from '@backstage/core-plugin-api';
import { RAIL_EYEBROW } from './railEyebrow';
import {
  catalogApiRef,
  entityRouteRef,
  EntityInfoCard,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';

/*
 * The mock's Ownership card: who owns this entity, in the rail rather than the
 * page header.
 *
 * Measured from the mock's own inline styles:
 *
 *   body    padding:14px 20px; flex column; gap:12px
 *   row     flex; align-items:center; gap:10px
 *   avatar  26x26; radius 8; #202020 on brand yellow; 10px/700
 *   name    13px/600 #202020
 *   meta    11px #6d6d6d  ("owner · #care-platform")
 *
 * The mock's second row is an on-call person ("M. Okafor · on-call until Fri").
 * The portal holds no on-call data — PagerDuty is installed but exposes it
 * through its own card, keyed off a per-entity annotation — so that row is
 * omitted rather than invented.
 *
 * The Slack channel is real: `GroupSlackChannelsProcessor` stamps each mapped
 * Group with a `Slack: #<name>` link alongside the `slack.com/bot-notify`
 * annotation (the annotation itself carries a channel ID, not a name). Groups
 * with no mapping simply show "owner".
 */

const SLACK_LINK_PREFIX = 'https://slack.com/app_redirect';

/*
 * Group and User pages are excluded: the org plugin's own Ownership card (the
 * count tiles) already holds that name there.
 */
const EXCLUDED_KINDS = new Set(['group', 'user']);

/** Whether the entity should show this card. */
export function showsOwnership(entity: Entity): boolean {
  if (EXCLUDED_KINDS.has(entity.kind.toLocaleLowerCase('en-US'))) {
    return false;
  }
  return Boolean(entity.relations?.some(r => r.type === RELATION_OWNED_BY));
}

/** The `#channel` from a Group's stamped Slack link, if it has one. */
export function slackChannel(group: Entity | undefined): string | undefined {
  const link = group?.metadata.links?.find(l =>
    l.url.startsWith(SLACK_LINK_PREFIX),
  );
  return link?.title?.replace(/^Slack:\s*/, '');
}

function displayName(group: Entity | undefined, fallback: string): string {
  const profile = group?.spec?.profile as { displayName?: string } | undefined;
  return profile?.displayName ?? group?.metadata.title ?? fallback;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': { textDecoration: 'none', color: 'inherit' },
    '&:hover $name': { textDecoration: 'underline' },
  },
  avatar: {
    flex: '0 0 auto',
    width: 26,
    height: 26,
    borderRadius: 8,
    background: 'var(--hone-portal-surface-solid)',
    color: 'var(--helix-colors-brand-500)',
    fontSize: 10,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'uppercase',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  name: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.4,
    color: 'var(--bui-fg-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    ...RAIL_EYEBROW,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export function EntityOwnershipCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const entityRoute = useRouteRef(entityRouteRef);

  const owners = getEntityRelations(entity, RELATION_OWNED_BY);
  const ownerRefs = owners.map(
    o => `${o.kind}:${o.namespace}/${o.name}`.toLocaleLowerCase('en-US'),
  );

  /*
   * The relation carries only a ref, and the display name and Slack channel
   * live on the Group itself, so the owners are read back from the catalog.
   */
  const { value: groups } = useAsync(async () => {
    if (ownerRefs.length === 0) {
      return [];
    }
    const response = await catalogApi.getEntitiesByRefs({
      entityRefs: ownerRefs,
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'metadata.links',
        'spec.profile',
      ],
    });
    return response.items;
  }, [catalogApi, ownerRefs.join(',')]);

  if (ownerRefs.length === 0) {
    return null;
  }

  return (
    <EntityInfoCard title="Ownership">
      <div className={classes.root}>
        {ownerRefs.map((ref, index) => {
          const group = groups?.[index];
          const { kind, namespace, name } = parseEntityRef(ref);
          const title = displayName(group, name);
          const channel = slackChannel(group);
          const to = entityRoute?.({ kind, namespace, name });

          return (
            <Link className={classes.row} to={to ?? ''} key={ref}>
              <span className={classes.avatar}>{title.charAt(0)}</span>
              <span className={classes.text}>
                <span className={classes.name}>{title}</span>
                <span className={classes.meta}>
                  {channel ? `owner · ${channel}` : 'owner'}
                </span>
              </span>
            </Link>
          );
        })}
      </div>
    </EntityInfoCard>
  );
}
